import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@components/common/Button';
import { APP_NAME, APP_DESCRIPTION, ORG_NAME } from '@utils/constants';
import { ArrowRight, Shield, FileText, Calendar, CheckCircle } from 'lucide-react';

export const Home: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <FileText className="h-8 w-8" />,
      title: 'Easy Online Application',
      description: 'Complete your aviation license application in simple, guided steps',
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: 'NIN Verification',
      description: 'Instant identity verification through NIMC integration',
    },
    {
      icon: <Calendar className="h-8 w-8" />,
      title: 'Book Appointments',
      description: 'Schedule your testing dates at convenient locations',
    },
    {
      icon: <CheckCircle className="h-8 w-8" />,
      title: 'Track Progress',
      description: 'Monitor your application status in real-time',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-ncaa-primary via-blue-700 to-ncaa-secondary text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              {APP_NAME}
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              {APP_DESCRIPTION}
            </p>
            <p className="text-lg mb-12 text-blue-100 max-w-3xl mx-auto">
              Apply for your aviation license online through the {ORG_NAME} digital platform.
              Streamlined, secure, and designed for aviation professionals.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="xl"
                onClick={() => navigate('/register')}
                className="bg-white text-ncaa-primary hover:bg-gray-100"
                icon={<ArrowRight className="h-5 w-5" />}
                iconPosition="right"
              >
                Start Your Application
              </Button>
              <Button
                variant="outline"
                size="xl"
                onClick={() => navigate('/login')}
                className="border-white text-white hover:bg-white hover:text-ncaa-primary"
              >
                Sign In
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-ncaa-dark mb-4">
              Why Choose {APP_NAME}?
            </h2>
            <p className="text-xl text-ncaa-medium max-w-3xl mx-auto">
              Our digital platform makes aviation licensing faster, more transparent, and more convenient than ever before.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-ncaa-primary text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-ncaa-dark mb-2">
                  {feature.title}
                </h3>
                <p className="text-ncaa-medium">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-ncaa-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-ncaa-dark mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-ncaa-medium mb-8 max-w-2xl mx-auto">
            Join thousands of aviation professionals who have successfully obtained their licenses through our platform.
          </p>
          <Button
            size="xl"
            onClick={() => navigate('/register')}
            icon={<ArrowRight className="h-5 w-5" />}
            iconPosition="right"
          >
            Begin Your Journey
          </Button>
        </div>
      </section>
    </div>
  );
};
